import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';

export default function Notifications() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/notifications/my')
      .then((res) => setList(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const notifyNavbar = () => window.dispatchEvent(new Event('notifications:changed'));

  const clearNotification = async (notification, shouldNavigate = false) => {
    setList((current) => current.filter((item) => item._id !== notification._id));
    notifyNavbar();

    try {
      await api.patch(`/notifications/${notification._id}/clear`);
      if (shouldNavigate && notification.link) navigate(notification.link);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not clear notification');
      const { data } = await api.get('/notifications/my').catch(() => ({ data: [] }));
      setList(data);
      notifyNavbar();
    }
  };

  const clearAll = async () => {
    const previous = list;
    setList([]);
    notifyNavbar();
    try {
      await api.patch('/notifications/clear-all');
      toast.success('Notifications cleared');
    } catch (error) {
      setList(previous);
      notifyNavbar();
      toast.error(error.response?.data?.message || 'Could not clear notifications');
    }
  };

  return (
    <div className="bg-[#FAF8F5] min-h-screen text-[#2B1810] py-10">
      <div className="max-w-3xl mx-auto px-4 space-y-6">
        <div className="text-center space-y-2 relative">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C49A45]">Activity & Alerts</span>
          <h1 className="font-heading text-3xl font-bold text-[#2B1810]">My Notifications</h1>
          <div className="flex items-center justify-center gap-3 mt-2">
            <span className="h-[1px] w-12 bg-[#C49A45]"></span>
            <span className="text-[#C49A45] text-lg">🔔</span>
            <span className="h-[1px] w-12 bg-[#C49A45]"></span>
          </div>
          {!loading && list.length > 0 && (
            <button type="button" onClick={clearAll} className="sm:absolute sm:right-0 sm:bottom-0 text-xs font-bold text-[#8A796C] hover:text-red-700 px-3 py-2">
              Clear all
            </button>
          )}
        </div>

        <div className="bg-white border border-[#E8DFD8] rounded-xl shadow-sm overflow-hidden divide-y divide-[#E8DFD8]">
          {loading ? (
            <p className="p-8 text-center text-xs text-[#8A796C]">Loading notifications...</p>
          ) : list.length === 0 ? (
            <div className="p-10 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-[#FAF8F5] border border-[#E8DFD8] flex items-center justify-center text-[#C49A45] mx-auto">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 01-3.46 0"/>
                </svg>
              </div>
              <h3 className="font-heading text-base font-bold text-[#2B1810]">No Notifications Yet</h3>
              <p className="text-xs text-[#8A796C]">You're all caught up! Order status updates and tracking alerts will appear here.</p>
              <Link to="/shop" className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-6 py-2.5 rounded shadow inline-block mt-2">
                BROWSE SHOP
              </Link>
            </div>
          ) : (
            list.map((n) => (
              <div key={n._id} className="p-4 sm:p-5 flex items-start gap-3 hover:bg-[#FAF8F5] transition-colors">
                <button type="button" onClick={() => clearNotification(n, true)} className="flex flex-1 min-w-0 items-start gap-4 text-left">
                <div className="w-9 h-9 rounded-full bg-[#F5EFEB] border border-[#C49A45]/30 flex items-center justify-center text-[#C49A45] shrink-0 mt-0.5">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 01-3.46 0"/>
                  </svg>
                </div>
                <div className="flex-1 space-y-1">
                  <h4 className="font-bold text-sm text-[#2B1810]">{n.title}</h4>
                  <p className="text-xs text-[#8A796C] leading-relaxed">{n.message}</p>
                  <p className="text-[10px] text-[#A8988B] font-medium">{new Date(n.createdAt).toLocaleString('en-IN')}</p>
                </div>
                </button>
                <button type="button" onClick={() => clearNotification(n)} className="w-9 h-9 shrink-0 flex items-center justify-center text-[#A8988B] hover:text-red-700" title="Clear notification" aria-label={`Clear ${n.title}`}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
